import { Injectable } from '@angular/core';
import { addDays, addHours, isAfter } from 'date-fns';
import { BehaviorSubject } from 'rxjs';
import { TennisOneLocalStore, Timeline } from '../_interfaces';
import { SsrLocalstoreService } from './ssr-localstore.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
    public store: BehaviorSubject<TennisOneLocalStore>;

    public testing_token = {
        "user": {
            "display_name": "",
            "first_name": "Ryan",
            "last_name": "Adolphson",
            "profile_img": "http://res.cloudinary.com/bleachr/image/upload/v1708103406/m4pe9jfaqbfx3ussxzuu.png",
            "fan_id": "dbd0a58e-d4a4-4962-80ec-7e654f3c5ec9",
            "fingerprint": "web-f378d2b2-0c83-4e99-9d1e-87c19011d326",
            "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmYW5faWQiOiJkYmQwYTU4ZS1kNGE0LTQ5NjItODBlYy03ZTY1NGYzYzVlYzkiLCJpYXQiOjE3Njk2NDI2MDIsImV4cCI6MTc2OTgxNTQwMn0.bWR0nyxmEFczHj-wWJCJM6KFx4wffQSrLkvmxuIrY-0",
            "ttl": 1769815402978
        }
    }

    constructor(private ssr: SsrLocalstoreService) {
        const initial = this.loadOrInitStore();
        this.store = new BehaviorSubject<TennisOneLocalStore>(initial);
    }

    private generateTTL(): number {
        return addDays(new Date(), 5).getTime();
    }

    private buildDefaults(existing?: Partial<TennisOneLocalStore>): TennisOneLocalStore {
        const existingFingerprint =
            existing?.user?.fingerprint ||
            (this.ssr.isClientSide ? this.ssr.getItem('tennisOne-fingerprint') : null) || `web-${uuidv4()}`;

        if (this.ssr.isClientSide && !this.ssr.getItem('tennisOne-fingerprint')) {
            this.ssr.setItem('tennisOne-fingerprint', existingFingerprint);
        }

        return {
            user: {
                display_name: existing?.user?.display_name ?? '',
                first_name: existing?.user?.first_name ?? '',
                last_name: existing?.user?.last_name ?? '',
                profile_img: existing?.user?.profile_img ?? '',
                fan_id: existing?.user?.fan_id ?? '',
                fingerprint: existingFingerprint,
                token: existing?.user?.token ?? null,
                ttl: existing?.user?.ttl ?? this.generateTTL()
            },
            timeline_hub: {
                cache: existing?.timeline_hub?.cache ?? [],
                ttl: existing?.timeline_hub?.ttl ?? addHours(new Date(), 1).getTime()
            }
        };
    }


    private loadOrInitStore(): TennisOneLocalStore {
        if (!this.ssr.isClientSide) {
            return this.buildDefaults();
        }

        const raw = this.ssr.getItem('tennisOne');

        if (!raw) {
            const defaults = this.buildDefaults();
            this.ssr.setItem('tennisOne', JSON.stringify(defaults));
            return defaults;
        }

        try {
            const parsed = JSON.parse(raw) as TennisOneLocalStore;
            const normalized = this.buildDefaults(parsed);
            this.ssr.setItem('tennisOne', JSON.stringify(normalized));
            return normalized;
        } catch {
            const defaults = this.buildDefaults();
            this.ssr.setItem('tennisOne', JSON.stringify(defaults));
            return defaults;
        }
    }

    async initLocalStore(): Promise<void> {
        if (!this.ssr.isClientSide) return;

        const next = this.loadOrInitStore();
        this.store.next(next);
    }

    getLocalStoreCache(): Timeline[] {
        return this.ssr.isClientSide ? (this.store.value.timeline_hub.cache ?? []) : [];
    }

    getLocalStore(): TennisOneLocalStore {
        if (!this.ssr.isClientSide) return this.buildDefaults();

        const raw = this.ssr.getItem('tennisOne');
        if (!raw) return this.buildDefaults();

        try {
            return this.buildDefaults(JSON.parse(raw) as TennisOneLocalStore);
        } catch {
            return this.buildDefaults();
        }
    }

    get__UserToken(): string {
        try {
            if (!this.ssr.isClientSide) {
                return ''
            }

            const local = this.ssr.getItem('tennisOne')

            if (!local) {
                return ''
            }

            if (local) {
                const local_json: TennisOneLocalStore = JSON.parse(local)

                if (isAfter(new Date(), new Date(local_json.user.ttl))) {
                    return ''
                }
                return local_json.user.token ?? ''
            }

            return ''

        } catch (err) {
            throw err
        }
    }

    setLocalStore(new_store: TennisOneLocalStore): void {

        if (this.ssr.isClientSide) {
            this.ssr.setItem('tennisOne', JSON.stringify(new_store));
        }

        this.store.next(new_store);
    }

    timelineCacheExpired(): boolean {
        if (!this.ssr.isClientSide) return true;

        const raw = this.ssr.getItem('tennisOne');
        if (!raw) return true;

        try {
            const locale_cache: TennisOneLocalStore = JSON.parse(raw);
            const ttl = locale_cache?.timeline_hub?.ttl;

            if (!ttl) return true;
            return isAfter(new Date(), new Date(ttl));
        } catch {
            return true;
        }
    }

    setTimelineCache(timeline_data: Timeline[]): void {
        const current = this.getLocalStore();

        const updated: TennisOneLocalStore = {
            ...current,
            timeline_hub: {
                ...current.timeline_hub,
                cache: timeline_data,
                ttl: addHours(new Date(), 1).getTime()
            }
        };

        if (this.ssr.isClientSide) {
            this.ssr.setItem('tennisOne', JSON.stringify(updated));
        }

        this.store.next(updated);
    }

    updateUserProperty(key: string, value: any): void {
        try {
            if (this.ssr.isClientSide) {
                const current = this.getLocalStore();

                const updated: TennisOneLocalStore = {
                    ...current,
                    user: {
                        ...current.user,
                        [key]: value
                    }
                };

                this.setLocalStore(updated);
            }
        } catch (err) {
            throw err
        }
    }

    clear(): void {
        if (!this.ssr.isClientSide) return;

        const local_keys = Object.keys(localStorage);

        local_keys.forEach((key) => {
            if (key !== 'preferredLanguage' && key !== 'whatsNewContentURL') {
                this.ssr.removeItem(key);
            }
        });

        const defaults = this.buildDefaults();
        this.ssr.setItem('tennisOne', JSON.stringify(defaults));
        this.store.next(defaults);
    }
}
